import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../services/api';
import GoogleAuthButton from './GoogleAuthButton';
import Navbar from '../shared/Navbar';

const getSafeRedirect = (redirect: string | null) => {
  if (!redirect || !redirect.startsWith('/') || redirect.startsWith('//')) return '/dashboard';
  return redirect;
};

const trackAccountCreation = (userId?: string | number) => {
  const gtag = (window as Window & {
    gtag?: (...args: unknown[]) => void;
  }).gtag;

  if (!gtag) return;

  gtag('event', 'conversion', {
    send_to: 'AW-18366916855/f9XaCPf66d4cEPfRg7ZE',
    value: 1.0,
    currency: 'GBP',
    transaction_id: userId == null ? '' : String(userId),
    new_customer: true,
  });
};

const RegisterPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTarget = getSafeRedirect(searchParams.get('redirect'));
  const onGoogleSuccess = useCallback((result: {
    created?: boolean;
    user?: { id?: string | number };
  }) => {
    if (result.created) {
      trackAccountCreation(result.user?.id);
    }
    navigate(redirectTarget);
  }, [navigate, redirectTarget]);

  useEffect(() => {
    (async () => {
      try {
        const me = await api.me();
        if (me?.authenticated) {
          navigate(redirectTarget);
        }
      } catch {}
    })();
  }, [navigate, redirectTarget]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await api.register(email, password);
      trackAccountCreation(result.user?.id);
      setEmailVerificationRequired(result.email_verification_required !== false);
      setRegistrationComplete(true);
    } catch (e: any) {
      setError(e?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app">
      <Navbar />
      <main className="app-main">
        <div className="content-container auth-container">
          <div className="tm-surface auth-card">
            <h2 className="auth-title">Create Account</h2>
            {registrationComplete ? (
              <>
                <p className="auth-subtitle">
                  {emailVerificationRequired ? (
                    <>
                      We sent a verification link to <strong>{email}</strong>. Open it
                      to finish creating your account.
                    </>
                  ) : (
                    <>Your account is ready. You can sign in now.</>
                  )}
                </p>
                <div className="auth-links">
                  <Link to={`/login?redirect=${encodeURIComponent(redirectTarget)}`}>
                    Back to sign in
                  </Link>
                </div>
              </>
            ) : (
              <>
              <p className="auth-subtitle">Join to save and resume your learning</p>
            <form onSubmit={onSubmit} className="login-form">
            <div className="form-row">
              <label>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required />
            </div>
            <div className="form-row">
              <label>Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} required />
            </div>
            {error && <div className="ml-error tm-mb-sm">{error}</div>}
            <button type="submit" className="tm-button tm-button--primary ml-replace-btn auth-submit" disabled={loading}>
              {loading ? 'Registering…' : 'Register'}
            </button>
            <div className="auth-separator"><span>or</span></div>
            <GoogleAuthButton
              text="signup_with"
              onSuccess={onGoogleSuccess}
              onError={setError}
            />
            <div className="auth-links">
              <Link to={`/login?redirect=${encodeURIComponent(redirectTarget)}`}>Already have an account? Sign in</Link>
              <span className="auth-divider">•</span>
              <Link to="/">Back to Home</Link>
            </div>
            </form>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default RegisterPage;
